import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, LoadingController, ModalController, NavController, PopoverController } from '@ionic/angular';
import Autolinker from 'autolinker';
import _ from 'lodash';
import moment from 'moment';

import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { ConversationsService } from 'src/services/conversations.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';
import { UsersService } from 'src/services/users.service';

import { Conversation, Profile } from '../../models';
import { UploadService } from '../../services/upload.service';
import { ActionDropdownComponent, ActionDropdownType } from '../components/action-dropdown/action-dropdown.component';
import { ComposeComponentOptions, ComposePage } from '../components/compose/compose.page';
import { AUTO_ADD_BOT_CONDITION, routes, USER_STATUS_ACTIVE, USER_STATUS_INACTIVE } from '../constants';
import { INBOX_SUBPAGES } from '../cx-home/cx-home.component';
import { delay, findMedian, formatSlackContent, generateCustomFieldsArray, getHtmlPreviewText } from '../utils/utils';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.page.html',
  styleUrls: ['./profile.page.scss'],
})
export class ProfilePage implements OnInit {
  profile: Profile;
  profileId: string;
  isExternal: boolean;
  isSelf: boolean;
  isAdmin: boolean;
  canEditEmail = true;
  canEditPhoto = true;
  defaultFields = [];
  customFields = [];
  allConversations: Partial<Conversation>[] = [];
  users: Partial<Profile>[];

  name = '';
  firstName = '';
  email = '';
  phone = '';
  description = '';

  title = '';
  department = '';
  organization = '';
  division = '';
  costCenter = '';
  employeeNumber = '';
  streetAddress = '';
  locality = '';
  region = '';
  postalCode = '';
  country = '';
  pronoun = '';
  slackId: string;

  isLoading = false;
  isInEditMode = false;
  isConnectingSlack = false;

  autolinker = new Autolinker();

  communities: any[];

  medianFirstResponse: string;
  medianFirstResponseLabel: string;
  medianResolution: string;
  medianResolutionLabel: string;

  autoAddBotCondition: string;
  autoAddBotChannelType: string;
  autoAddBotRegex: string | null;
  autoAddBotInternalOverride: boolean | null;
  isAway = false;
  originalWayUntil: Date;
  awayUntil: Date;
  backupUserId: string;
  isAutoAssignable: boolean = null;
  isNewConversationNotificationEnabled: boolean = null;
  isAssignmentNotificationEnabled: boolean = null;
  emailSignature: string;
  hasUserChatWriteScope: boolean;
  hasUserChannelScope: boolean;
  hasUserReactionScope: boolean;
  defaultInboxView: any;

  constructor(
    public msgSrvc: MessageService,
    private uploadSrvc: UploadService,
    private loadingCtrl: LoadingController,
    private popoverCtrl: PopoverController,
    public authService: AuthService,
    private route: ActivatedRoute,
    private navController: NavController,
    private modalCtrl: ModalController,
    private apiService: ApiService,
    private usersService: UsersService,
    private alertCtrl: AlertController,
    private analyticsService: AnalyticsService,
    private conversationsService: ConversationsService,
    private slackService: SlackService,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  async ngOnInit() {
    const user = this.authService.user;
    this.isAdmin = this.authService.isTenantAdmin;

    // Angular auth will take a while to initialize
    // It'll be available a few seconds after the app is loaded
    if (user) {
      this.configureForAuthProvider();
    }

    this.profileId = this.route.snapshot.paramMap.get('profileId');
    this.isExternal = this.route.snapshot.paramMap.get('type') === 'external';

    if (!this.profileId || (user && this.profileId === this.authService.userId)) {
      this.isSelf = true;
      this.profileId = this.authService.userId;
    }

    if (!this.isSelf) {
      this.canEditEmail = false;
      this.canEditPhoto = false;
    }

    const shouldEdit = this.route.snapshot.queryParamMap.get('edit');

    if (shouldEdit && (this.isSelf || this.isAdmin)) {
      this.isInEditMode = true;
    }

    // Set some properties that will be edited
    if (this.isSelf && user) {
      this.name = user.displayName;
      this.email = user.email;
      this.phone = user.phoneNumber;
      this.profileId = this.authService.userId;
    }

    await this.loadProfile();
    this.getSlackScopes();

    if (this.profile) {
      this.getCustomers();
    }

    this.loadConversations();

    if (this.isSelf || this.isAdmin) {
      this.loadUsers();
    }
  }

  async getSlackScopes() {
    this.isConnectingSlack = true;
    const [hasUserChatWriteScope, hasChannelJoinScope, hasUserReactionScope] = await Promise.all([
      this.slackService.hasUserScope('chat:write', true),
      this.slackService.hasUserScope('channels:read', true),
      this.slackService.hasUserScope('reactions:write', true),
    ]);

    this.hasUserChatWriteScope = hasUserChatWriteScope;
    this.hasUserChannelScope = hasChannelJoinScope;
    this.hasUserReactionScope = hasUserReactionScope;
    this.isConnectingSlack = false;
  }

  async loadProfile() {
    const profile: Profile = await this.authService.getProfileById(this.profileId, this.isExternal);

    if (profile) {
      this.profile = profile;
      this.name = this.profile.name;
      this.email = this.profile.email;
      this.description = this.profile.description;
      this.phone = this.profile.phoneNumber;
      this.autoAddBotChannelType = this.profile.autoAddBotSettings.channelType;
      this.autoAddBotCondition = this.profile.autoAddBotSettings.condition;
      this.autoAddBotRegex = this.profile.autoAddBotSettings.regex ?? '';
      this.autoAddBotInternalOverride = this.profile.autoAddBotSettings.isInternalOverride;
      this.backupUserId = this.profile.backupUserId;
      this.isAway = this.profile.awayUntil ? moment(this.profile.awayUntil).isAfter(moment()) : false;
      this.awayUntil = this.profile.awayUntil ?? moment().add(7, 'days').toDate();
      this.originalWayUntil = this.awayUntil;
      this.isAutoAssignable = this.profile.isAutoAssignable;
      this.slackId = this.profile.slackId;
      this.emailSignature = this.profile.emailSignature;
      this.isNewConversationNotificationEnabled = this.profile.notificationSettings?.conversationCreated ?? false;
      this.isAssignmentNotificationEnabled = this.profile.notificationSettings?.conversationAssignedToMe ?? true;

      const userHasSavedFilter = !!profile.defaultInboxView?.trim().length;
      const maybeSavedFilter = profile.defaultInboxView?.split('filter/')[1]; // Undefined if default filter, otherwise saved filter id
      let savedView = null;

      // Load saved filter for user if exists
      if (userHasSavedFilter) {
        if (maybeSavedFilter) {
          const savedFilter: any = await this.apiService.getPromise(`/saved-filters/${maybeSavedFilter}`);
          savedView = { title: savedFilter.name, id: profile.defaultInboxView, object: savedFilter };
        } else {
          savedView = INBOX_SUBPAGES.find((page) => page.url === profile.defaultInboxView);
        }
      }
      this.defaultInboxView = savedView ?? INBOX_SUBPAGES[0];

      this.defaultFields = [];

      this.addDefaultField('pronoun', 'Pronoun');
      this.addDefaultField('title', 'Title');
      this.addDefaultField('department', 'Department');
      this.addDefaultField('organization', 'Organization');
      this.addDefaultField('division', 'Division');
      this.addDefaultField('costCenter', 'Cost Center');
      this.addDefaultField('employeeNumber', 'Employee ID');
      this.addDefaultField('streetAddress', 'Street Address');
      this.addDefaultField('locality', 'City');
      this.addDefaultField('region', 'Region');
      this.addDefaultField('postalCode', 'Postal Code');
      this.addDefaultField('country', 'Country');

      this.customFields = generateCustomFieldsArray(
        this.authService.customProfileFields,
        this.profile.customFields,
        this.profile.additionalIdpFields,
      );
    }
  }

  addDefaultField(id: string, title: string) {
    const value = this.profile[id];

    this.defaultFields.push({
      id,
      title,
      public: this.authService.defaultFieldSettings?.[id]?.public ?? false,
      value,
    });
    this[id] = value;
  }

  configureForAuthProvider() {
    this.authService.user.providerData.map((provider) => {
      if (
        provider.providerId.includes('oidc') ||
        provider.providerId.includes('saml') ||
        provider.providerId.includes('google.com') ||
        provider.providerId.includes('microsoft.com')
      ) {
        this.canEditEmail = false;
        return;
      }
    });
  }

  async authorizeSlack() {
    if (this.isConnectingSlack) {
      return;
    }

    this.isConnectingSlack = true;
    this.slackService.userOauth(`${window.location.origin}/close-window`, window.location.origin);

    await delay(5000);

    window.location.reload();
  }

  async loadUsers() {
    const userList = await this.usersService.list({
      limit: null,
      order: ['name'],
      descending: false,
      showInactiveUsers: false,
    });

    this.users = userList.data.filter((u) => {
      return u.id !== this.profileId;
    });
  }

  selectDefaultInboxView() {
    this.searchablePopoverService.inboxViews({
      event,
      side: 'bottom',
      callback: (id: string, name: string, object: any) => {
        this.defaultInboxView = {
          title: name,
          id,
          object,
        };
      },
    });
  }

  async showAdminOptions() {
    const isActive = this.profile.status === USER_STATUS_ACTIVE;
    const options: ActionDropdownType[] = [
      {
        label: isActive ? 'Deactivate user' : 'Reactivate user',
        icon: isActive ? 'close-circle-outline' : 'add-circle-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (_index: number, _label: string) => {
          popover.dismiss();

          // Deactivate user
          this.isLoading = true;
          const newStatus = isActive ? USER_STATUS_INACTIVE : USER_STATUS_ACTIVE;
          await this.usersService.updateUser(this.profileId, {
            status: newStatus,
          });
          this.profile.status = newStatus;
          this.isLoading = false;
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async sendMessage() {
    const email = this.profile.email;
    if (!email) {
      this.msgSrvc.show('No email found for user');
      return;
    }

    const options: ComposeComponentOptions = {
      title: 'Send a message',
      hideEmails: true,
    };

    const modal = await this.modalCtrl.create({
      component: ComposePage,
      componentProps: {
        emails: [email],
        replyTo: this.authService.user.email,
        senderId: this.authService.userId,
        options,
      },
    });
    modal.present();
  }

  async save(showSuccess?: boolean) {
    if (!this.name || !this.email) {
      this.msgSrvc.show('You need to have a name and email');
      return;
    }

    if (this.isSelf) {
      const user = this.authService.user;

      if (user.email !== this.email) {
        await this.updateEmail();
      }
    }

    await this.updateProfile(showSuccess);
    await this.loadProfile();
  }

  async updateProfile(showSuccess?: boolean) {
    for (const req of this.customFields) {
      if (req.value === undefined) {
        req.value = null;
      }

      if (req.required && !req.value) {
        this.msgSrvc.show('Please fill out all required fields');
        return;
      }
    }

    const updates: Partial<Profile> = {
      name: this.name,
      autoAddBotSettings: {
        ...this.profile.autoAddBotSettings,
        condition: this.autoAddBotCondition,
        channelType: this.autoAddBotChannelType,
        regex:
          this.autoAddBotCondition === AUTO_ADD_BOT_CONDITION.ALWAYS && this.autoAddBotRegex
            ? this.autoAddBotRegex
            : null,
        isInternalOverride: this.autoAddBotInternalOverride ?? null,
      },
      notificationSettings: {
        ...this.profile.notificationSettings,
        conversationCreated: this.isNewConversationNotificationEnabled,
        conversationAssignedToMe: this.isAssignmentNotificationEnabled,
      },
      backupUserId: this.backupUserId ?? null,
    };

    if (this.pronoun != null) {
      updates.pronoun = this.pronoun;
    }

    if (this.title != null) {
      updates.title = this.title;
    }

    if (this.department != null) {
      updates.department = this.department;
    }

    if (this.organization != null) {
      updates.organization = this.organization;
    }

    if (this.division != null) {
      updates.division = this.division;
    }

    if (this.costCenter != null) {
      updates.costCenter = this.costCenter;
    }

    if (this.employeeNumber != null) {
      updates.employeeNumber = this.employeeNumber;
    }

    if (this.streetAddress != null) {
      updates.streetAddress = this.streetAddress;
    }

    if (this.locality != null) {
      updates.locality = this.locality;
    }

    if (this.region != null) {
      updates.region = this.region;
    }

    if (this.postalCode != null) {
      updates.postalCode = this.postalCode;
    }

    if (this.country != null) {
      updates.country = this.country;
    }

    if (this.description != null) {
      updates.description = this.description;
    }

    if (this.phone != null) {
      updates.phoneNumber = this.phone;
    }

    if (this.customFields.length) {
      updates.customFields = this.customFields;
    }

    if (this.isAway) {
      updates.awayUntil = this.awayUntil;
    } else {
      updates.awayUntil = null;
    }

    if (this.isAutoAssignable !== undefined) {
      updates.isAutoAssignable = this.isAutoAssignable;
    }

    if (this.emailSignature) {
      updates.emailSignature = this.emailSignature;
    }

    if (this.defaultInboxView) {
      updates.defaultInboxView = this.defaultInboxView.id;
    }

    this.isLoading = true;

    try {
      await this.usersService.updateUser(this.profileId, updates, this.isExternal);

      this.isLoading = false;
      this.isInEditMode = false;

      if (showSuccess) {
        this.msgSrvc.show('✓ Saved');
      }
    } catch (error) {
      this.isLoading = false;
      this.msgSrvc.show(`Unable to save. Here's the error: ${error.message || 'connection failed'}`);
    }
  }

  async updateEmail() {
    this.isLoading = true;

    const user = this.authService.user;

    try {
      await this.usersService.updateEmail(this.profileId, this.email, user);

      this.isLoading = false;
    } catch (err) {
      this.isLoading = false;
      this.email = user.email;

      this.msgSrvc.show(err.message);
    }
  }

  async detectProfilePhotoUpload(event) {
    const files = event.target.files;

    if (files.length) {
      const file = event.target.files.item(0);

      const loader = await this.loadingCtrl.create({
        message: 'Uploading...',
        spinner: 'crescent',
        duration: 10000,
      });

      loader.present();

      try {
        await this.uploadSrvc.uploadProfilePicture(this.authService.userId, file);

        this.profile.photo = this.authService.userPhoto;
      } catch (err) {
        this.msgSrvc.show(err.message);
      } finally {
        loader.dismiss();
      }
    }
  }

  getPreviewText(text: string) {
    return getHtmlPreviewText(text, 180);
  }

  async getCustomers() {
    this.communities = await this.apiService.getPromise(`/users/${this.profileId}/customers`);
  }

  viewCustomer(customer: any) {
    const route = `${routes.DASHBOARD}/${routes.CUSTOMERS}/${customer.id}/overview`;

    this.navController.navigateForward(route);
  }

  async loadConversations() {
    const options: any = {
      limit: 50,
      order: ['createdAt'],
      descending: true,
      where: [],
      select: [
        'id',
        'respondedAt',
        'closedAt',
        'createdAt',
        'friendlyId',
        'initialMessage.id',
        'initialMessage.text',
        'initialMessage.content',
        'status',
      ],
    };

    if (this.isExternal) {
      options.where.push({
        field: 'initialMessage.sentByUserId',
        operator: '==',
        value: this.profileId,
      });
    } else {
      options.where.push({
        field: 'assignedToUserId',
        operator: '==',
        value: this.profileId,
      });
    }

    try {
      if (options.where.length) {
        const convos = await this.conversationsService.list(options);
        this.allConversations = convos.data;
      } else {
        this.allConversations = [];
      }

      this.analyzeConversationList();
    } catch (e) {
      console.log('Error loading conversations: ', e);
    }
  }

  analyzeConversationList() {
    if (!this.allConversations?.length) {
      return;
    }

    const resolutionTimes: number[] = [];
    const firstResponseTimes: number[] = [];

    this.allConversations.map((conversation) => {
      const createdAt = moment(conversation.createdAt);

      if (conversation.respondedAt) {
        firstResponseTimes.push(moment(conversation.respondedAt).diff(createdAt, 'minutes'));
      }

      if (conversation.closedAt) {
        resolutionTimes.push(moment(conversation.closedAt).diff(createdAt, 'minutes'));
      }
    });

    const medianFirstResponseMinutes = findMedian(firstResponseTimes);
    const medianFirstResponseHours = Math.round(medianFirstResponseMinutes / 60);
    const medianFirstResponseDays = Math.round(medianFirstResponseMinutes / 60 / 24);
    if (medianFirstResponseDays) {
      this.medianFirstResponse = `${medianFirstResponseDays}`;
      this.medianFirstResponseLabel = `day${medianFirstResponseDays === 1 ? '' : 's'}`;
    } else if (medianFirstResponseHours) {
      this.medianFirstResponse = `${medianFirstResponseHours}`;
      this.medianFirstResponseLabel = 'hr';
    } else if (medianFirstResponseMinutes) {
      this.medianFirstResponse = `${medianFirstResponseMinutes}`;
      this.medianFirstResponseLabel = `min`;
    } else if (medianFirstResponseMinutes === 0) {
      this.medianFirstResponse = '< 1';
      this.medianFirstResponseLabel = `min`;
    } else {
      this.medianFirstResponse = `n/a`;
      this.medianFirstResponseLabel = ``;
    }

    const medianResolutionMinutes = findMedian(resolutionTimes);
    const medianResolutionHours = Math.round(medianResolutionMinutes / 60);
    const medianResolutionDays = Math.round(medianResolutionMinutes / 60 / 24);
    if (medianResolutionDays) {
      this.medianResolution = `${medianResolutionDays}`;
      this.medianResolutionLabel = `day${medianResolutionDays === 1 ? '' : 's'}`;
    } else if (medianResolutionHours) {
      this.medianResolution = `${medianResolutionHours}`;
      this.medianResolutionLabel = 'hr';
    } else if (medianResolutionMinutes) {
      this.medianResolution = `${medianResolutionMinutes}`;
      this.medianResolutionLabel = `min`;
    } else {
      this.medianResolution = `n/a`;
      this.medianResolutionLabel = ``;
    }
  }

  async showProfileOptions(profile: Profile) {
    const isActive = this.profile.status === USER_STATUS_ACTIVE;

    const SHARE = 'Copy profile link';
    const EMAIL = 'Copy email address';
    const EDIT = 'Edit profile';
    const REPORT = 'Report this profile';
    const REQUEST_ACCOUNT_DELETION = 'Request account deletion';
    const ACTIVATE = 'Reactivate user';
    const DEACTIVATE = 'Deactivate user';
    const LOGOUT = 'Logout';

    const options: ActionDropdownType[] = [
      {
        label: SHARE,
        icon: 'link-outline',
      },
      {
        label: EMAIL,
        icon: 'link-outline',
      },
    ];

    if (this.isSelf || this.isAdmin) {
      options.push({
        label: EDIT,
        icon: 'create-outline',
      });
    }

    if (!this.isSelf) {
      options.push({
        label: REPORT,
        icon: 'flag-outline',
        destructive: true,
      });
    }

    if (this.isAdmin && !this.isSelf) {
      options.push({
        label: isActive ? DEACTIVATE : ACTIVATE,
        icon: isActive ? 'close-circle-outline' : 'add-circle-outline',
        destructive: isActive,
      });
    }

    if (this.isSelf) {
      options.push({
        label: REQUEST_ACCOUNT_DELETION,
        icon: 'trash-bin-outline',
        destructive: true,
      });
    }

    if (profile.id === this.authService.userId) {
      options.push({
        label: LOGOUT,
        icon: 'log-out-outline',
      });
    }

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (_index: number, label: string) => {
          popover.dismiss();

          if (label === REPORT) {
            this.reportProfile(profile);
          } else if (label === SHARE) {
            this.copyLink(location.href);
          } else if (label === EMAIL) {
            this.copyLink(`mailto:${profile.email}`);
          } else if (label === EDIT) {
            this.isInEditMode = true;
          } else if (label === LOGOUT) {
            this.authService.logout();
          } else if (label === REQUEST_ACCOUNT_DELETION) {
            this.requestAccountDeletion(profile);
          } else if (label === ACTIVATE || label === DEACTIVATE) {
            this.toggleIsActive();
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async toggleIsActive() {
    const isActive = this.profile.status === USER_STATUS_ACTIVE;
    // Actiate or deactivate user
    if (this.isSelf) {
      this.msgSrvc.show('You cannot deactivate or reactivate yourself');
      return;
    }
    this.isLoading = true;
    const newStatus = isActive ? USER_STATUS_INACTIVE : USER_STATUS_ACTIVE;
    await this.usersService.updateUser(this.profileId, {
      status: newStatus,
    });
    this.profile.status = newStatus;
    this.isLoading = false;
  }

  async reportProfile(profile: Profile) {
    const confirm = await this.alertCtrl.create({
      header: `Report this profile`,
      inputs: [
        {
          type: 'textarea',
          placeholder: 'Enter any details (optional)',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          handler: () => {},
        },
        {
          text: 'Save',
          handler: async (data: any[]) => {
            const notes = data[0];
            const request = this.apiService.post(`/me/make-report`, {
              type: 'moderate-profile',
              objectId: profile.id,
              notes,
            });

            this.isLoading = true;

            request.subscribe(
              async (_result) => {
                this.isLoading = false;
                this.msgSrvc.show('This profile has been reported');
              },
              async (err) => {
                this.msgSrvc.show(err.message || 'Unable to send');
                this.isLoading = false;
              },
            );
          },
        },
      ],
    });
    confirm.present();

    this.analyticsService.trackEvent('Profile Details', 'Report profile');
  }

  async requestAccountDeletion(profile: Profile) {
    const confirm = await this.alertCtrl.create({
      header: `Request account deletion`,
      subHeader:
        'Support will be notified of your request. You may be contacted to provide additional information in order to process your request.',
      inputs: [
        {
          type: 'textarea',
          placeholder: 'Enter any details (optional)',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          handler: () => {},
        },
        {
          text: 'Send',
          handler: async (data: any[]) => {
            const notes = data[0];
            const request = this.apiService.post(`/me/make-report`, {
              type: 'delete-profile',
              objectId: profile.id,
              notes,
            });

            this.isLoading = true;

            request.subscribe(
              async (_result) => {
                this.isLoading = false;
                this.msgSrvc.show('Your request has been sent.');
              },
              async (err) => {
                this.msgSrvc.show(err.message || 'Unable to send');
                this.isLoading = false;
              },
            );
          },
        },
      ],
    });

    confirm.present();

    this.analyticsService.trackEvent('Profile Details', 'Request account deletion');
  }

  async copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      this.msgSrvc.show('✓ Copied link to clipboard');
    } catch {
      this.msgSrvc.show('Unable to copy link');
    }
  }

  autoAddBotCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  getInitialDate() {
    return moment(this.originalWayUntil).format('YYYY-MM-DD');
  }

  getInitialTime() {
    return moment(this.originalWayUntil).format('HH:mm');
  }

  setAwayUntil([date, time]) {
    this.awayUntil = moment(`${date} ${time}`).toDate();
  }

  formatContent(content: any[]) {
    return formatSlackContent(content);
  }

  isAutoAssignableCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  isAutoAssignableDefaultLabel() {
    const isUserAutoAssignableDefault = this.authService.tenant.isUserAutoAssignableDefault ?? true;

    if (isUserAutoAssignableDefault === true) {
      return 'Yes';
    }

    return 'No';
  }
}
